import { type AppLinkOptions, appLinks } from 'src/lib/routing';
import type { BillType, BuySellOrder, GrowerProduct, PurchaseOrder, SalesOrder } from 'types/graphql';

// TODO: migrate WorkOrder references to GrowerProduct

export enum EntityType {
  SALES_ORDER = 'SalesOrder',
  PURCHASE_ORDER = 'PurchaseOrder',
  WORK_ORDER = 'WorkOrder',
  GROWER_PRODUCT = 'GrowerProduct',
  BUY_SELL_ORDER = 'BuySellOrder',
  PRODUCTION_RUN = 'ProductionRun',
  LOT = 'Lot',
  BILL = 'Bill',
}

export type OrderTypename = NonNullable<(SalesOrder | PurchaseOrder | BuySellOrder | GrowerProduct)['__typename']>;

type EntityRouteParams = {
  [EntityType.SALES_ORDER]: { slug: string };
  [EntityType.PURCHASE_ORDER]: { slug: string };
  [EntityType.WORK_ORDER]: { slug: string };
  [EntityType.GROWER_PRODUCT]: { slug: string };
  [EntityType.BUY_SELL_ORDER]: { slug: string };
  [EntityType.PRODUCTION_RUN]: { slug: string };
  [EntityType.LOT]: { slug: string; warehouseId: number };
  [EntityType.BILL]: { slug: string };
};

type EntityRouteRegistry = {
  [entityType in EntityType]: (params: EntityRouteParams[entityType]) => AppLinkOptions;
};

type EntityIdKeyByType = {
  [EntityType.SALES_ORDER]: 'salesOrderId';
  [EntityType.PURCHASE_ORDER]: 'purchaseOrderId';
  [EntityType.WORK_ORDER]: 'workOrderId';
  [EntityType.GROWER_PRODUCT]: 'growerProductId';
  [EntityType.BUY_SELL_ORDER]: 'buySellOrderId';
  [EntityType.PRODUCTION_RUN]: 'productionRunId';
  [EntityType.LOT]: 'lotId';
  [EntityType.BILL]: 'billId';
};

type EntityTypeRegistry = {
  [entityType in EntityType]: {
    route: EntityRouteRegistry[entityType];
    prefix: string;
    id: EntityIdKeyByType[entityType];
    statuses: string[];
    name: string;
  };
};

type OrderReferenceEntityType = OrderEntityType | EntityType.PRODUCTION_RUN;
type OrderReference = Partial<Record<EntityIdKeyByType[OrderReferenceEntityType], number | null>>;

export const entityRoutes = {
  [EntityType.SALES_ORDER]: (params) => appLinks.salesOrder(params),
  [EntityType.PURCHASE_ORDER]: (params) => appLinks.purchaseOrder(params),
  [EntityType.WORK_ORDER]: (params) => appLinks.growerProduct(params),
  [EntityType.GROWER_PRODUCT]: (params) => appLinks.growerProduct(params),
  [EntityType.BUY_SELL_ORDER]: (params) => appLinks.buySellOrder(params),
  [EntityType.PRODUCTION_RUN]: (params) => appLinks.productionRun(params),
  [EntityType.LOT]: (params) => appLinks.viewLotTracking(params),
  [EntityType.BILL]: (params) => appLinks.bill(params),
} satisfies EntityRouteRegistry;
export type OrderEntityType =
  | EntityType.SALES_ORDER
  | EntityType.PURCHASE_ORDER
  | EntityType.WORK_ORDER
  | EntityType.BUY_SELL_ORDER;
export type OrderRouteName = 'salesOrder' | 'purchaseOrder' | 'growerProduct' | 'buySellOrder';

export const orderEntityTypesByRouteName = {
  salesOrder: EntityType.SALES_ORDER,
  purchaseOrder: EntityType.PURCHASE_ORDER,
  growerProduct: EntityType.WORK_ORDER,
  buySellOrder: EntityType.BUY_SELL_ORDER,
} satisfies Record<OrderRouteName, OrderEntityType>;

export const orderRoutesByTypename = {
  SalesOrder: entityRoutes[EntityType.SALES_ORDER],
  PurchaseOrder: entityRoutes[EntityType.PURCHASE_ORDER],
  GrowerProduct: entityRoutes[EntityType.GROWER_PRODUCT],
  BuySellOrder: entityRoutes[EntityType.BUY_SELL_ORDER],
} satisfies Record<OrderTypename, EntityRouteRegistry[OrderEntityType]>;

export const billListRoutes = {
  INVOICE: () => appLinks.invoices(),
  PAYABLE: () => appLinks.payables(),
} satisfies Record<BillType, () => AppLinkOptions>;

export const entityTypes: EntityTypeRegistry = {
  SalesOrder: {
    route: entityRoutes[EntityType.SALES_ORDER],
    prefix: 'SO',
    id: 'salesOrderId',
    statuses: ['DRAFT', 'SUBMITTED', 'PICKING', 'SHIPPED', 'CLOSED', 'CANCELLED', 'ERROR'],
    name: 'Sales Order',
  },
  PurchaseOrder: {
    route: entityRoutes[EntityType.PURCHASE_ORDER],
    prefix: 'PO',
    id: 'purchaseOrderId',
    statuses: ['DRAFT', 'SUBMITTED', 'RECEIVING', 'PENDING_LIQUIDATION', 'CLOSED', 'CANCELLED', 'ERROR'],
    name: 'Purchase Order',
  },
  WorkOrder: {
    route: entityRoutes[EntityType.WORK_ORDER],
    prefix: 'GP',
    id: 'workOrderId',
    statuses: ['DRAFT', 'SUBMITTED', 'RECEIVING', 'PENDING_SETTLEMENT', 'CLOSED'],
    name: 'Grower Product',
  },
  GrowerProduct: {
    route: entityRoutes[EntityType.GROWER_PRODUCT],
    prefix: 'GP',
    id: 'growerProductId',
    statuses: ['DRAFT', 'SUBMITTED', 'RECEIVING', 'PENDING_SETTLEMENT', 'CLOSED'],
    name: 'Grower Product',
  },
  BuySellOrder: {
    route: entityRoutes[EntityType.BUY_SELL_ORDER],
    prefix: 'BSO',
    id: 'buySellOrderId',
    statuses: ['DRAFT', 'SUBMITTED', 'PAYMENT_RECONCILIATION', 'CLOSED'],
    name: 'Buy-Sell Order',
  },
  ProductionRun: {
    route: entityRoutes[EntityType.PRODUCTION_RUN],
    prefix: 'PR',
    id: 'productionRunId',
    statuses: ['DRAFT', 'SUBMITTED', 'CLOSED'],
    name: 'Production Run',
  },
  Lot: {
    route: entityRoutes[EntityType.LOT],
    prefix: '',
    id: 'lotId',
    statuses: ['OPEN', 'LOCKED', 'CLOSED'],
    name: 'Lot',
  },
  Bill: {
    route: entityRoutes[EntityType.BILL],
    prefix: 'B',
    id: 'billId',
    statuses: ['DRAFT', 'OPEN', 'CLOSED', 'CANCELLED'],
    name: 'Bill',
  },
};

const orderReferenceEntityTypes: readonly OrderReferenceEntityType[] = [
  EntityType.SALES_ORDER,
  EntityType.PURCHASE_ORDER,
  EntityType.WORK_ORDER,
  EntityType.BUY_SELL_ORDER,
  EntityType.PRODUCTION_RUN,
];
const orderReferenceIdKeys = orderReferenceEntityTypes.map((entityType) => entityTypes[entityType].id);

/**
 * Finds the existing order id and returns the id key
 */
export const getOrderIdKeyById = (objWithOrder: OrderReference) => {
  const orderType = orderReferenceIdKeys.find((key) => {
    return !!objWithOrder[key];
  });

  return orderType;
};

/**
 * Finds the existing order id and returns the order type
 */
export const getOrderTypeById = (objWithOrder: OrderReference) => {
  const orderType = orderReferenceEntityTypes.find((orderType) => {
    const id = entityTypes[orderType].id;

    return !!objWithOrder[id];
  });

  return orderType;
};

export const getOrderByPresense = (objWithOrder: {
  purchaseOrder?: unknown;
  salesOrder?: unknown;
  workOrder?: unknown;
  buySellOrder?: unknown;
}) => {
  const { purchaseOrder, salesOrder, workOrder, buySellOrder } = objWithOrder;
  const order = purchaseOrder || salesOrder || workOrder || buySellOrder;

  return order;
};
